import React, { useState, ChangeEvent } from "react";
import { Container, RadioButtonWithLabel } from "../../../components";
import { Heading, Button } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import AcceptContent from "./AcceptContent";
import InformationBlocks from "./InformationBlocks";
import RejectContent from "./RejectContent";
import { RootState } from "../../../../../models";
import { RejectReason, RejectReasonID } from "../../../../../models/transfer";
import { isBankIdVerified as isBankIdVerifiedSelector } from "../../../authentication/state/selectors";
import RenderVerification from "../../../checkout/components/organisms/Verification/RenderVerification";
import { VerificationDeliveryMethod } from "../../../checkout/consts";
import {
    getExistingVerifiedCompanyAdmin,
    getTransferExistingOrganizationName,
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
    shouldShowPinVerification as showPinVerification,
    getAdminNumber,
} from "../../../checkout/state/selectors/accountSelectors";
import { getOrganizationName } from "../../../checkout/state/selectors/businessDetailsSelectors";
import TEXT from "../../text-consts";
interface Props {
    breakageFee: number;
    isB2BTransfer: boolean;
    rejectAction: (reasonText: string, reasonId: string) => void;
    sendPinAction: (phoneNumber: string, deliveryMethod: VerificationDeliveryMethod) => void;
    submitAction: () => void;
    verifyPinAction: (phoneNumber: string, pin: string) => void;
}

const ApprovalChoices = ({
    breakageFee,
    isB2BTransfer,
    submitAction,
    rejectAction,
    sendPinAction,
    verifyPinAction,
}: Props) => {
    const isBankIdVerified = useSelector((state: RootState) => isBankIdVerifiedSelector(state));
    const brRegOrganizationName = useSelector(
        (state: RootState) => getOrganizationName(state) || getTransferExistingOrganizationName(state) || "",
    );
    const isExistingLegalOwnerVerified = useSelector((state: RootState) => isExistingLegalOwnerVerifiedSelector(state));
    const shouldShowPinVerification = useSelector((state: RootState) => showPinVerification(state));
    const adminNumber = useSelector((state: RootState) => getAdminNumber(state));
    const [shouldShowVerification, setShouldShowVerification] = useState(false);

    const verifiedCompanyAdmin = useSelector((state: RootState) => getExistingVerifiedCompanyAdmin(state));
    const [selectedOption, setSelectedOption] = useState<"accept" | "reject" | null>(
        isBankIdVerified ? "accept" : null,
    );
    const [subSelectedOption, setSubSelectedOption] = useState<"accept-b2c" | "accept-b2b" | null>(
        isBankIdVerified && brRegOrganizationName ? "accept-b2b" : isBankIdVerified ? "accept-b2c" : null,
    );
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [hasClickedRejected, setHasClickedRejected] = useState(false);
    const [rejectReasons, setRejectReasons] = useState<RejectReasonID | null>(null);
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectReasonInput, setRejectReasonInput] = useState(false);
    const reasons: RejectReason[] = [
        { id: "NOT_OWNER", text: "Jeg er ikke eier av dette nummeret" },
        { id: "NOT_FAMILIAR_NUMBER", text: "Jeg kjenner ikke til at dette nummeret skal overføres" },
        { id: "OTHER", text: "Annet", reason: rejectReason },
    ];

    if (breakageFee) {
        reasons.splice(2, 0, { id: "NOT_ACCEPTABLE_FEE", text: "Jeg aksepterer ikke bruddgebyret" });
    }

    const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        setRejectReason((e.target as HTMLInputElement).value);
    };

    const handleSubmitTransferOwnershipOrder = () => {
        setHasClickedSubmit(true);
        submitAction();
    };

    const handleRejectReasons = (id: RejectReasonID) => {
        if (id === "OTHER") {
            setRejectReasonInput(!showRejectReasonInput);
        }
        setRejectReasons(id);
    };

    const handleRejectTransferOwnershipOrder = () => {
        setHasClickedRejected(true);
        const reason = reasons.find((r) => r.id === rejectReasons);

        if (rejectReasons && reason) {
            let reasonText = "";
            let reasonId = "OTHER";

            if (rejectReasons === "OTHER" && rejectReason) {
                reasonText = rejectReason;
            } else {
                reasonText = reason.text;
                reasonId = rejectReasons;
            }

            rejectAction(reasonText, reasonId);
        }
    };

    const submitButtonText = {
        B2B: TEXT.CONSENT_FORMER_LEGAL.BUTTON.NEXT_STEP,
        B2C: TEXT.CONSENT_FORMER_LEGAL.BUTTON.CONFIRM,
    }[isB2BTransfer ? "B2B" : "B2C"];

    return (
        <>
            <Container size="medium" whiteBg className="consent-landing-page__section">
                <div className="consent-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="accept"
                        onChange={(event) => {
                            setSelectedOption(event.target.value as "accept" | "reject");
                            setRejectReasons(null);
                            if (!isB2BTransfer) {
                                setSubSelectedOption("accept-b2c");
                            } else {
                                setSubSelectedOption(null);
                            }
                        }}
                        name="consentB2B"
                        checked={selectedOption === "accept"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="consent-landing-page__consent-radio-heading"
                        >
                            {"Jeg godkjenner overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {selectedOption === "accept" && (
                        <AcceptContent
                            isB2BTransfer={isB2BTransfer}
                            subSelectedOption={subSelectedOption}
                            setSubSelectedOption={setSubSelectedOption}
                            setRejectReasons={setRejectReasons}
                        />
                    )}
                </div>
                <div className="consent-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="reject"
                        onChange={(event) => {
                            setSelectedOption(event.target.value as "accept" | "reject");
                            setSubSelectedOption(null);
                            setRejectReasons(null);
                        }}
                        name="consentB2B"
                        checked={selectedOption === "reject"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="consent-landing-page__consent-radio-heading"
                        >
                            {"Jeg avslår overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {selectedOption === "reject" && (
                        <RejectContent
                            handleReasonChange={handleReasonChange}
                            handleRejectReasons={handleRejectReasons}
                            reasons={reasons}
                            rejectReason={rejectReason}
                            rejectReasons={rejectReasons}
                            setSubSelectedOption={setSubSelectedOption}
                        />
                    )}
                </div>
            </Container>
            {!isB2BTransfer && isExistingLegalOwnerVerified && <InformationBlocks.ExistingOwnerInformation />}
            <Container size="medium" className="consent-landing-page__right-align">
                {selectedOption === "accept" && isBankIdVerified && (
                    <Button
                        variant="primary"
                        disabled={!(isExistingLegalOwnerVerified || verifiedCompanyAdmin) || hasClickedSubmit}
                        onClick={handleSubmitTransferOwnershipOrder}
                    >
                        {submitButtonText}
                    </Button>
                )}
                {selectedOption === "reject" && rejectReasons && (
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={hasClickedRejected}
                        onClick={handleRejectTransferOwnershipOrder}
                    >
                        {TEXT.CONSENT_FORMER_LEGAL.BUTTON.NEXT_STEP}
                    </Button>
                )}
            </Container>
            {!_isEmpty(adminNumber) && (
                <Container size="medium" whiteBg className="consent-landing-page__section">
                    {!shouldShowVerification && shouldShowPinVerification && !isExistingLegalOwnerVerified && (
                        <Button
                            type="button"
                            variant="primary"
                            className="consent-landing-page__bankid-button"
                            onClick={(): void => setShouldShowVerification(true)}
                        >
                            {TEXT.CONSENT_FORMER_LEGAL.BUTTON.ONE_TIME_PIN}
                        </Button>
                    )}
                    {shouldShowVerification && shouldShowPinVerification && (
                        <RenderVerification
                            validatePhoneNumber={false}
                            selectedPhoneNumber={adminNumber}
                            sendCode={async (...args): Promise<void> => sendPinAction(...args)}
                            verifyCode={async (...args): Promise<void> => verifyPinAction(...args)}
                            data-di-mask
                        />
                    )}
                </Container>
            )}
        </>
    );
};

export default ApprovalChoices;
